import React from 'react';
import { AraTableRow, fetchAraData, fetchAraYears, fetchSubordinates, fetchDivisions, Subordinate, Division, BillDetailRow, fetchBillDetails, BillSortKey, PaymentTypeValue, fetchAgeingByUser, AraUserTableRow, AraUserSortKey, DueStatus, downloadBillDetails, downloadBulkAgeingData, CustomerStatusTypeValue, fetchConsignors, Consignor, downloadAgeingData, downloadRDDProvision } from './api';
import AraTable from './components/AraTable';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import AraDropdowns from "./components/AraDropdowns";
import BillDetailsTable from './components/BillDetails';
import AraByUserTable from './components/AraByUserTable';
import { useAuth } from 'contexts/AuthContext';

const SORT_COLUMN_MAP: Record<string, string> = {
    Customer: 'customer',
    CreditPeriod: 'credit_period',
    TotalOutstanding: 'total_outstanding',
    NotDue: 'not_due',
    TotalOverdue: 'total_overdue',
    Bucket1_30: 'bucket_1_30',
    Bucket31_60: 'bucket_31_60',
    Bucket61_90: 'bucket_61_90',
    Bucket91_120: 'bucket_91_120',
    Bucket121_180: 'bucket_121_180',
    BucketOver180: 'bucket_over_180',
    Legal: 'legal',
    UnappliedReceipts: 'unapplied_receipts',
    NetOutstanding: 'net_outstanding',
    DSO: 'dso',
};

const fyOrder = (m: number) => (m >= 4 ? m - 3 : m + 9);

const AraPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const { permissions } = useAuth();
    const [search, setSearch] = React.useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');

    const [yearMonthDayMap, setYearMonthDayMap] = React.useState<Record<string, Record<number, number[]>>>({});
    const [years, setYears] = React.useState<string[]>([]);
    const [availableMonths, setAvailableMonths] = React.useState<number[]>([]);
    const [availableDays, setAvailableDays] = React.useState<number[]>([]);
    const [year, setYear] = React.useState<string>('');
    const [month, setMonth] = React.useState<number | null>(null);
    const [day, setDay] = React.useState<number | null>(null);

    const [divisions, setDivisions] = React.useState<Division[]>([]);
    const [selectedDivision, setSelectedDivision] = React.useState<string>('');

    const [subordinates, setSubordinates] = React.useState<Subordinate[]>([]);
    const [selectedEcode, setSelectedEcode] = React.useState<string>('');

    const [paymentType, setPaymentType] = React.useState<PaymentTypeValue>('');
    const [customerStatus, setCustomerStatus] = React.useState<CustomerStatusTypeValue>('');
    const [fromDate, setFromDate] = React.useState<string>('');
    const [toDate, setToDate] = React.useState<string>('');

    const [consignors, setConsignors] = React.useState<Consignor[]>([]);
    const [selectedConsignor, setSelectedConsignor] = React.useState<string>('');

    const [selectedCustomer, setSelectedCustomer] = React.useState<string | null>(null);
    const [selectedUser, setSelectedUser] = React.useState<string | null>(null);
    const [selectedUserName, setSelectedUserName] = React.useState<string | null>(null);
    const [billDetails, setBillDetails] = React.useState<BillDetailRow[]>([]);
    const [billLoading, setBillLoading] = React.useState<boolean>(false);
    const [billSearch, setBillSearch] = React.useState<string>('');
    const [debouncedBillSearch, setDebouncedBillSearch] = React.useState('');
    const [billDueStatus, setBillDueStatus] = React.useState<DueStatus | undefined>(undefined);

    const [downloadingBulkAgeingExcel, setDownloadingBulkAgeingExcel] = React.useState<boolean>(false);
    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    const [downloadingRDDExcel, setDownloadingRDDExcel] = React.useState<boolean>(false);

    const [userAgeingData, setUserAgeingData] = React.useState<AraUserTableRow[]>([]);
    const [userAgeingLoading, setUserAgeingLoading] = React.useState(false);
    const [userAgeingPage, setUserAgeingPage] = React.useState(1);
    const [userAgeingPageSize, setUserAgeingPageSize] = React.useState(20);
    const [userAgeingTotalItems, setUserAgeingTotalItems] = React.useState(0);


    const [tableData, setTableData] = React.useState<AraTableRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');

    // Pagination
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [totalItems, setTotalItems] = React.useState(0);

    const [billPage, setBillPage] = React.useState(1);
    const [billPageSize, setBillPageSize] = React.useState(20);
    const [billTotalItems, setBillTotalItems] = React.useState(0);

    // Sorting
    const [sortBy, setSortBy] = React.useState<keyof AraTableRow>('TotalOutstanding');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const [userSortBy, setUserSortBy] = React.useState<AraUserSortKey>('total_outstanding');
    const [userSortOrder, setUserSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const [billSortBy, setBillSortBy] = React.useState<BillSortKey>('due_date');
    const [billSortOrder, setBillSortOrder] = React.useState<'asc' | 'desc'>('asc');

    const hasLoadedOnce = React.useRef(false);

    const canDownloadExcel = React.useMemo(() => {
        return Array.isArray(permissions) && permissions.includes("ara.download");
    }, [permissions]);

    const canDownloadRDDProvision = React.useMemo(() => {
        return Array.isArray(permissions) && permissions.includes("outstanding.rdd_provision.download");
    }, [permissions]);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: 'Reports', href: '/reports' },
            { label: 'Accounts Receivable Ageing' },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [search]);

    React.useEffect(() => {
        const loadDivisions = async () => {
            try {
                const resp = await fetchDivisions();
                setDivisions(resp.items);
                if (resp.items.length === 1) {
                    setSelectedDivision(resp.items[0].ou_code);
                }
            } catch {
                setDivisions([]);
            }
        };
        loadDivisions();
    }, []);

    React.useEffect(() => {
        const loadSubordinates = async () => {
            try {
                const resp = await fetchSubordinates(selectedDivision || undefined);
                setSubordinates(resp.items);
                // Don't auto-select ecode anymore - it's optional
                setSelectedEcode('');
            } catch {
                setSubordinates([]);
            }
        };
        loadSubordinates();
    }, [selectedDivision]);

    React.useEffect(() => {
        const loadYears = async () => {
            try {
                const resp = await fetchAraYears();

                const map: Record<string, Record<number, number[]>> = {};
                const yearList: string[] = [];

                resp.years.forEach(({ year, months }) => {
                    map[year] = months;
                    yearList.push(year);
                });

                setYearMonthDayMap(map);
                setYears(yearList);

                if (yearList.length) {
                    const defaultYear = yearList[0];
                    const monthMap = map[defaultYear] ?? {};

                    const sortedMonths = Object.keys(monthMap)
                        .map(Number)
                        .sort((a, b) => fyOrder(b) - fyOrder(a));

                    if (sortedMonths.length) {
                        const latestMonth = sortedMonths[0];
                        const days = map[defaultYear][latestMonth] ?? [];
                        const sortedDays = [...days].sort((a, b) => b - a);

                        setYear(defaultYear);
                        setAvailableMonths(sortedMonths);
                        setMonth(latestMonth);
                        setAvailableDays(sortedDays);
                        setDay(sortedDays[0] ?? null);
                    }
                }
            } catch {
                setYearMonthDayMap({});
                setYears([]);
            }
        };
        loadYears();
    }, []);

    React.useEffect(() => {
        if (!year || !month || !day) return;

        const loadConsignors = async () => {
            try {
                const resp = await fetchConsignors({
                    year,
                    month,
                    day,
                    ecode: selectedEcode || undefined,
                });
                setConsignors(resp.items);
            } catch {
                setConsignors([]);
            }
        };
        loadConsignors();
    }, [year, month, day, selectedEcode]);

    React.useEffect(() => {
        if (!year || !month || !day || !selectedCustomer || !selectedUser) return;

        const loadBills = async () => {
            setBillLoading(true);
            try {
                const resp = await fetchBillDetails({
                    customer_name: selectedCustomer,
                    year,
                    month,
                    day,
                    ecode: selectedUser || undefined,
                    division: selectedDivision || undefined,
                    page: billPage,
                    per_page: billPageSize,
                    sort_by: billSortBy,
                    sort_order: billSortOrder,
                    search: debouncedBillSearch || undefined,
                    due_status: billDueStatus || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined
                });

                setBillDetails(resp.items);
                setBillTotalItems(resp.total_items);
            } finally {
                setBillLoading(false);
            }
        };

        loadBills();
    }, [selectedCustomer, selectedUser, year, month, day, selectedDivision, billPage, billPageSize, billSortBy, billSortOrder, debouncedBillSearch, billDueStatus, fromDate, toDate]);

    React.useEffect(() => {
        if (!year || !month || !day) return;

        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const resp = await fetchAraData({
                    year,
                    month,
                    day,
                    ecode: selectedEcode || undefined,
                    division: selectedDivision || undefined,
                    payment_type: paymentType || undefined,
                    customer_status: customerStatus || undefined,
                    consignor_code: selectedConsignor || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                    page,
                    per_page: pageSize,
                    sort_by: SORT_COLUMN_MAP[sortBy],
                    sort_order: sortOrder,
                    search: debouncedSearch || undefined,
                });

                setTableData(resp?.rows ?? []);
                setTotalItems(resp?.total_items ?? 0);

                // Set selectedEcode from API response on initial load if not already set
                if (!hasLoadedOnce.current && !selectedEcode && resp?.selected_user) {
                    setSelectedEcode(resp.selected_user);
                }
            } catch (err: any) {
                const apiMessage = err?.response?.data?.detail || err?.response?.data?.message || err?.message;

                if (err?.response?.status === 403) {
                    setError(apiMessage || "You don't have access to this menu");
                } else {
                    setError(apiMessage || 'Failed to fetch ageing data');
                }
            } finally {
                setLoading(false);
                hasLoadedOnce.current = true;
            }
        };
        load();
    }, [year, month, day, selectedEcode, selectedDivision, paymentType, customerStatus, selectedConsignor, fromDate, toDate, page, pageSize, sortBy, sortOrder, debouncedSearch]);

    React.useEffect(() => {
        if (!year || !month || !day || !selectedCustomer) return;

        const loadUserAgeing = async () => {
            setUserAgeingLoading(true);
            try {
                const resp = await fetchAgeingByUser({
                    customer_name: selectedCustomer,
                    year,
                    month,
                    day,
                    ecode: selectedEcode || undefined,
                    division: selectedDivision || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                    page: userAgeingPage,
                    per_page: userAgeingPageSize,
                    sort_by: userSortBy,
                    sort_order: userSortOrder,
                });

                setUserAgeingData(resp.rows);
                setUserAgeingTotalItems(resp.total_items);
            } finally {
                setUserAgeingLoading(false);
            }
        };

        loadUserAgeing();
    }, [selectedCustomer, year, month, day, selectedEcode, selectedDivision, fromDate, toDate, userAgeingPage, userAgeingPageSize, userSortBy, userSortOrder]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedBillSearch(billSearch.trim());
        }, 500);

        return () => clearTimeout(handler);
    }, [billSearch]);

    const handleSortChange = (column: keyof AraTableRow) => {
        setPage(1);
        if (sortBy === column) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    const billDateSuffix = fromDate && toDate
        ? `_Bills_${fromDate}_to_${toDate}`
        : fromDate ? `_Bills_from_${fromDate}` : toDate ? `_Bills_upto_${toDate}` : '';

    const handleDownloadBulkAgeingExcel = async () => {
        if (!year || !month || !day) return;
        try {
            setDownloadingBulkAgeingExcel(true);
            const blob = await downloadBulkAgeingData({
                year,
                month,
                day,
                ecode: selectedEcode || undefined,
                division: selectedDivision || undefined,
                payment_type: paymentType || undefined,
                customer_status: customerStatus || undefined,
                consignor_code: selectedConsignor || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                search: debouncedSearch || undefined,
                sort_by: SORT_COLUMN_MAP[sortBy],
                sort_order: sortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Ageing_Data_${year}_${month}_${day}${billDateSuffix}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Ageing excel download failed', err);
        } finally {
            setDownloadingBulkAgeingExcel(false);
        }
    };

    const handleDownloadAgeingExcel = async () => {
        if (!year || !month || !day) return;

        try {
            setDownloadingExcel(true);
            const blob = await downloadAgeingData({
                year,
                month,
                day,
                ecode: selectedEcode || undefined,
                division: selectedDivision || undefined,
                payment_type: paymentType || undefined,
                customer_status: customerStatus || undefined,
                consignor_code: selectedConsignor || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                search: debouncedSearch || undefined,
                sort_by: SORT_COLUMN_MAP[sortBy],
                sort_order: sortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Ageing_Current_Page_${year}_${month}_${day}${billDateSuffix}.xlsx`;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Current page excel download failed', err);
        } finally {
            setDownloadingExcel(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!year || !month || !day || !selectedCustomer || !selectedUser) return;
        try {
            setDownloadingBulkAgeingExcel(true);

            const blob = await downloadBillDetails({
                customer_name: selectedCustomer,
                year,
                month,
                day,
                ecode: selectedUser || undefined,
                division: selectedDivision || undefined,
                search: debouncedBillSearch || undefined,
                due_status: billDueStatus || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                sort_by: billSortBy,
                sort_order: billSortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            a.download = `Bill_Details_${selectedCustomer}_${selectedUserName}_${year}_${month}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Excel download failed', err);
        } finally {
            setDownloadingBulkAgeingExcel(false);
        }
    };

    const handleDownloadRDDProvision = async () => {
        if (!year || !month || !day) return;

        try {
            setDownloadingRDDExcel(true);

            const blob = await downloadRDDProvision({ year, month, day });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `RDD_Provision_${year}_${month}_${day}.xlsx`;

            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('RDD Provision download failed', err);
        } finally {
            setDownloadingRDDExcel(false);
        }
    };

    const handleCustomerClick = async (customerName: string) => {
        if (!year || !month) return;

        setSelectedCustomer(customerName);
        setSelectedUser(null);
        setUserAgeingPage(1);
        setUserAgeingLoading(true);
    };

    const handleUserClick = async (ecode: string, name: string) => {
        if (!year || !month || !selectedCustomer) return;

        setSelectedUser(ecode);
        setSelectedUserName(name);
        setBillPage(1);
        setBillSearch('');
        setBillDueStatus(undefined);
    };

    const handleBillSearchChange = (value: string) => {
        setBillSearch(value);
        setBillPage(1);
    };

    const handleBillDueStatusChange = (
        value: DueStatus | undefined
    ) => {
        setBillDueStatus(value);
        setBillPage(1);
    };


    const handlePageSizeChange = (size: number) => {
        setPage(1);
        setPageSize(size);
    };

    const handleYearChange = (y: string) => {
        setYear(y);

        const monthMap = yearMonthDayMap[y] ?? {};
        const months = Object.keys(monthMap).map(Number).sort((a, b) => fyOrder(b) - fyOrder(a));

        setAvailableMonths(months);

        if (months.length) {
            const firstMonth = months[0];
            const days = monthMap[firstMonth] ?? [];
            const sortedDays = [...days].sort((a, b) => b - a);

            setMonth(firstMonth);
            setAvailableDays(sortedDays);
            setDay(sortedDays[0] ?? null);
        } else {
            setMonth(null);
            setAvailableDays([]);
            setDay(null);
        }

        setPage(1);
    };

    const handleMonthChange = (m: number) => {
        setMonth(m);

        const days = yearMonthDayMap[year]?.[m] ?? [];
        const sortedDays = [...days].sort((a, b) => b - a);

        setAvailableDays(sortedDays);
        setDay(sortedDays[0] ?? null);
        setPage(1);
    };

    const handleDivisionChange = (divisionOuCode: string) => {
        setSelectedDivision(divisionOuCode);
        setSelectedEcode('');
        setPage(1);
    };

    const handleEcodeChange = (ecode: string) => {
        setSelectedEcode(ecode);
        setSelectedConsignor('');
        setPage(1);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleConversionChange = React.useCallback((type: 'INR' | 'Lakhs' | 'Crores') => {
        setConversionType(type);
    }, []);

    const handleUserSortChange = (key: AraUserSortKey) => {
        setUserAgeingPage(1);

        if (userSortBy === key) {
            setUserSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setUserSortBy(key);
            setUserSortOrder('asc');
        }
    };

    const handleBillSortChange = (col: BillSortKey) => {
        if (billSortBy === col) {
            setBillSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setBillSortBy(col);
            setBillSortOrder('asc');
        }

        setBillPage(1);
    };

    const handleBillPageChange = (page: number) => {
        setBillPage(page);
    };

    const handleBillPageSizeChange = (size: number) => {
        setBillPage(1);
        setBillPageSize(size);
    };

    const handleBillBack = () => {
        setSelectedCustomer(null);
    };

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6 pb-4">
                {selectedCustomer && selectedUser ? (
                    <div className="flex flex-col h-full min-h-0">
                        <BillDetailsTable
                            data={billDetails}
                            loading={billLoading}
                            customerName={selectedCustomer}
                            collectionResponsibilityName={selectedUserName}
                            page={billPage}
                            pageSize={billPageSize}
                            totalItems={billTotalItems}
                            sortBy={billSortBy}
                            sortOrder={billSortOrder}
                            onSortChange={handleBillSortChange}
                            onPageChange={handleBillPageChange}
                            onPageSizeChange={handleBillPageSizeChange}
                            onBack={() => setSelectedUser(null)}
                            conversionType={conversionType}
                            onConversionChange={setConversionType}
                            search={billSearch}
                            dueStatus={billDueStatus}
                            onSearchChange={handleBillSearchChange}
                            onDueStatusChange={handleBillDueStatusChange}
                            downloadingExcel={downloadingBulkAgeingExcel}
                            onDownloadExcel={handleDownloadExcel}
                        />
                    </div>
                ) : selectedCustomer ? (
                    <div className="flex flex-col h-full min-h-0">
                        <AraByUserTable
                            data={userAgeingData}
                            loading={userAgeingLoading}
                            customerName={selectedCustomer}
                            onUserClick={handleUserClick}
                            onBack={() => setSelectedCustomer(null)}
                            conversionType={conversionType}
                            onConversionChange={setConversionType}
                            page={userAgeingPage}
                            pageSize={userAgeingPageSize}
                            totalItems={userAgeingTotalItems}
                            onPageChange={(p) => setUserAgeingPage(p)}
                            onPageSizeChange={(s) => {
                                setUserAgeingPage(1);
                                setUserAgeingPageSize(s);
                            }}
                            sortBy={userSortBy}
                            sortOrder={userSortOrder}
                            onSortChange={handleUserSortChange}
                        />

                    </div>
                ) : (
                    <div className="flex flex-col h-full min-h-0">
                        <div className="shrink-0 pb-6">
                            <AraDropdowns
                                years={years}
                                year={year}
                                month={month}
                                day={day}
                                months={availableMonths}
                                days={availableDays}
                                divisions={divisions}
                                selectedDivision={selectedDivision}
                                subordinates={subordinates}
                                selectedEcode={selectedEcode}
                                search={search}
                                fromDate={fromDate}
                                toDate={toDate}
                                paymentType={paymentType}
                                customerStatus={customerStatus}
                                consignors={consignors}
                                selectedConsignor={selectedConsignor}
                                conversionType={conversionType}
                                onConversionChange={handleConversionChange}
                                onCustomerStatusChange={(val) => {
                                    setCustomerStatus(val);
                                    setPage(1);
                                }}
                                onPaymentTypeChange={(val) => {
                                    setPaymentType(val);
                                    setSelectedConsignor('');
                                    setPage(1);
                                }}
                                onSearchChange={handleSearchChange}
                                onFromDateChange={(val) => {
                                    setFromDate(val);
                                    setPage(1);
                                }}
                                onToDateChange={(val) => {
                                    setToDate(val);
                                    setPage(1);
                                }}
                                onYearChange={handleYearChange}
                                onMonthChange={handleMonthChange}
                                onDayChange={setDay}
                                onDivisionChange={handleDivisionChange}
                                onEcodeChange={handleEcodeChange}
                                onConsignorChange={setSelectedConsignor}
                                downloadingBulkExcel={downloadingBulkAgeingExcel}
                                onDownloadBulkExcel={handleDownloadBulkAgeingExcel}
                                downloadingExcel={downloadingExcel}
                                onDownloadExcel={handleDownloadAgeingExcel}
                                showDownloadExcelButton={canDownloadExcel}
                                downloadingRDDExcel={downloadingRDDExcel}
                                onDownloadRDDExcel={handleDownloadRDDProvision}
                                showDownloadRDDButton={canDownloadRDDProvision}
                            />
                        </div>

                        <div className="flex-1 min-h-0 space-y-4">
                            {error ? (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                    <p className="text-red-600 text-sm">{error}</p>
                                </div>
                            ) : (
                                <AraTable
                                    data={tableData}
                                    loading={loading}
                                    page={page}
                                    pageSize={pageSize}
                                    totalItems={totalItems}
                                    sortBy={sortBy}
                                    sortOrder={sortOrder}
                                    onSortChange={handleSortChange}
                                    onPageChange={setPage}
                                    onPageSizeChange={handlePageSizeChange}
                                    hasLoadedOnce={hasLoadedOnce.current}
                                    hasFiltersSelected={Boolean(year && month)}
                                    error={error}
                                    conversionType={conversionType}
                                    onConversionChange={handleConversionChange}
                                    onCustomerClick={handleCustomerClick}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AraPage;
